import { useCallback, useMemo, useState } from 'react'

import useApiPost from 'Pods/Shared/Hooks/useApiPost'
import useResendCountdown from 'Pods/Shared/Hooks/useResendCountdown'
import usePasswordLogin from 'Pods/Shared/User/Hooks/usePasswordLogin'
import useTranslation from 'Pods/ThreeDStudio/Hooks/useTranslation'

import { CODE_LENGTH, MODE, OAUTH_PATHS, STEP } from '../constants'

const EMPTY_CODE = Array.from({ length: CODE_LENGTH }, () => '')
const REQUEST_PATH = '/guests/email_codes'
const VERIFY_PATH = '/guests/email_codes/verify'
const SUCCESS_REDIRECT = '/ai'
const SUCCESS_REDIRECT_DELAY_MS = 1500
const LOGIN_CAPTCHA_ID = 'ai-sign-in-login-captcha'

// Some accounts (e.g. 2FA-enrolled) are refused by the OTP verify endpoint
// and must finish through the password form instead.
const USE_PASSWORD_LOGIN_ERROR = 'use_password_login'

function isUsePasswordLoginError(error) {
  return error?.response?.data?.error === USE_PASSWORD_LOGIN_ERROR
}

function readConferenceEvent() {
  if (typeof window === 'undefined') return null
  return new URLSearchParams(window.location.search).get('conference') || null
}

// Owns the multi-step sign-in flow (auth -> code -> success): email/code state,
// the mocked-friendly /guests/email_codes request + verify calls, social OAuth
// redirects and the post-verify redirect. The component is left as pure view.
function useAiSignIn({ initialMode = MODE.SIGNUP, initialStep = STEP.AUTH } = {}) {
  const [mode, setMode] = useState(initialMode)
  const [step, setStep] = useState(initialStep)
  const [email, setEmail] = useState('')
  const [optIn, setOptIn] = useState(true)
  const [digits, setDigits] = useState(EMPTY_CODE)
  const [hasCodeError, setHasCodeError] = useState(false)
  const [password, setPassword] = useState('')
  const [passwordError, setPasswordError] = useState('')
  const [showPasswordRequiredNotice, setShowPasswordRequiredNotice] = useState(false)
  const [isTwoFaRequired, setIsTwoFaRequired] = useState(false)
  const [twoFaCode, setTwoFaCode] = useState('')

  const { postData } = useApiPost()
  const {
    submitLogin,
    isLoading: isPasswordSubmitting,
    captchaEnabled,
  } = usePasswordLogin({ captchaId: LOGIN_CAPTCHA_ID })
  const {
    secondsLeft: resendSecondsLeft,
    start: startResendCountdown,
    stop: stopResendCountdown,
  } = useResendCountdown()
  const t = useTranslation()
  const conferenceEvent = useMemo(readConferenceEvent, [])

  const handleEmailChange = useCallback((event) => {
    setEmail(event.target.value)
    setPasswordError('')
  }, [])

  // Routes accounts the OTP endpoints refuse (use_password_login) straight to
  // the password step; returns false for every other error so callers keep
  // their own handling.
  const switchToPasswordIfRequired = useCallback((error) => {
    if (!isUsePasswordLoginError(error)) return false

    setShowPasswordRequiredNotice(true)
    setPasswordError('')
    setStep(STEP.PASSWORD)
    return true
  }, [])

  const requestCode = useCallback(() => {
    postData({
      path: REQUEST_PATH,
      data: { email: email.trim(), conference_event: conferenceEvent },
      onSuccess: () => {
        setDigits(EMPTY_CODE)
        setHasCodeError(false)
        setStep(STEP.CODE)
      },
      onError: (error) => { switchToPasswordIfRequired(error) },
    })
  }, [postData, email, conferenceEvent, switchToPasswordIfRequired])

  const handleSwitchMode = useCallback(() => {
    setMode(current => (current === MODE.SIGNUP ? MODE.LOGIN : MODE.SIGNUP))
    setStep(STEP.AUTH)
  }, [])

  const handleDigitsChange = useCallback((next) => {
    setHasCodeError(false)
    setDigits(next)

    if (!next.every(Boolean)) {
      return
    }

    postData({
      path: VERIFY_PATH,
      data: {
        email: email.trim(),
        code: next.join(''),
        conference_event: conferenceEvent,
        opt_in: optIn,
      },
      onSuccess: () => {
        setStep(STEP.SUCCESS)
        setTimeout(() => { window.location.assign(SUCCESS_REDIRECT) }, SUCCESS_REDIRECT_DELAY_MS)
      },
      onError: (error) => {
        if (switchToPasswordIfRequired(error)) return
        setHasCodeError(true)
      },
    })
  }, [postData, email, conferenceEvent, optIn, switchToPasswordIfRequired])

  const handleResend = useCallback(() => {
    setDigits(EMPTY_CODE)
    setHasCodeError(false)
    // Arm the cooldown immediately so rapid clicks can't fire duplicate
    // requests; a failed request resets it so the user can retry at once.
    startResendCountdown()
    postData({
      path: REQUEST_PATH,
      data: { email: email.trim(), conference_event: conferenceEvent },
      onError: (error) => {
        if (switchToPasswordIfRequired(error)) return
        stopResendCountdown()
      },
    })
  }, [postData, email, conferenceEvent, startResendCountdown, stopResendCountdown,
    switchToPasswordIfRequired])

  const handlePasswordChange = useCallback((event) => {
    setPassword(event.target.value)
    setPasswordError('')
  }, [])

  const handleTwoFaCodeChange = useCallback((event) => {
    setTwoFaCode(event.target.value)
    setPasswordError('')
  }, [])

  const handleSwitchToPassword = useCallback(() => {
    setPasswordError('')
    setShowPasswordRequiredNotice(false)
    setStep(STEP.PASSWORD)
  }, [])

  const handleBackFromPassword = useCallback(() => {
    setPassword('')
    setTwoFaCode('')
    setIsTwoFaRequired(false)
    setPasswordError('')
    setShowPasswordRequiredNotice(false)
    setStep(STEP.AUTH)
  }, [])

  const handlePasswordLogin = useCallback(() => {
    submitLogin({
      email: email.trim(),
      password,
      twoFaCode: isTwoFaRequired ? twoFaCode.trim() : null,
      onSuccess: () => {
        setStep(STEP.SUCCESS)
        setTimeout(() => { window.location.assign(SUCCESS_REDIRECT) }, SUCCESS_REDIRECT_DELAY_MS)
      },
      onTwoFaRequired: () => {
        setIsTwoFaRequired(true)
        setPasswordError('')
      },
      onError: ({ message }) => {
        setPasswordError(message || t('signIn.password.genericError'))
      },
    })
  }, [submitLogin, email, password, isTwoFaRequired, twoFaCode, t])

  const handleSocial = useCallback((provider) => {
    const oauthPath = OAUTH_PATHS[provider]
    if (!oauthPath) return

    // OmniAuth 2.x only accepts the request phase as a POST with a CSRF token;
    // a GET navigation is rejected with "Not found. Authentication passthru.".
    const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')

    const form = document.createElement('form')
    form.method = 'post'
    form.action = oauthPath

    if (token) {
      const tokenInput = document.createElement('input')
      tokenInput.type = 'hidden'
      tokenInput.name = 'authenticity_token'
      tokenInput.value = token
      form.appendChild(tokenInput)
    }

    document.body.appendChild(form)
    form.submit()
  }, [])

  let ariaLabel = t(mode === MODE.LOGIN ? 'signIn.modal.loginAriaLabel' : 'signIn.modal.signupAriaLabel')
  if (step === STEP.PASSWORD) {
    ariaLabel = t('signIn.password.title')
  }
  if (step === STEP.SUCCESS) {
    ariaLabel = t('signIn.modal.successAriaLabel')
  }

  return {
    mode,
    step,
    email,
    optIn,
    digits,
    hasCodeError,
    password,
    passwordError,
    showPasswordRequiredNotice,
    isPasswordSubmitting,
    isTwoFaRequired,
    twoFaCode,
    captchaEnabled,
    resendSecondsLeft,
    ariaLabel,
    setOptIn,
    handleEmailChange,
    requestCode,
    handleSwitchMode,
    handleDigitsChange,
    handleResend,
    handlePasswordChange,
    handleTwoFaCodeChange,
    handleSwitchToPassword,
    handleBackFromPassword,
    handlePasswordLogin,
    handleSocial,
  }
}

export default useAiSignIn
